import {
  DISPLAY_NAME_MAX,
  LOCALES,
  type Locale,
  type MeDto,
  NICKNAME_MAX,
  UpdateMeBody,
} from '@brig/api-types';
import { Vignette } from '@brig/theme/ornaments';
import { button, form, panel, paper } from '@brig/theme/styles';
import { useId, useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError } from '../api/client.ts';
import { useMe, useUpdateMe } from '../api/hooks.ts';
import { Cartouche } from '@brig/theme/components';
import { LoadingNotice } from '../components/LoadingNotice.tsx';
import { ENDONYMS } from '../i18n/index.ts';
import { useDocumentTitle } from '../useDocumentTitle.ts';
import * as styles from './Profile.css.ts';

type FieldErrors = Partial<Record<'nickname' | 'displayName', string | undefined>>;

/** The captain's own papers: particulars, the register entry and the language. */
export function Profile() {
  const { t } = useTranslation();
  useDocumentTitle(t('profile.pageTitle'));
  const me = useMe();

  if (me.isPending) return <LoadingNotice />;
  if (me.isError) {
    return (
      <section className={paper} role="alert">
        <p>{t('profile.loadFailed')}</p>
        <button type="button" className={button({ tone: 'label' })} onClick={() => void me.refetch()}>
          {t('profile.retry')}
        </button>
      </section>
    );
  }
  return <ProfileContent me={me.data} />;
}

function ProfileContent({ me }: { me: MeDto }) {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' });

  return (
    <div className={styles.page}>
      <Cartouche tone="paper">{t('profile.title')}</Cartouche>

      <div className={styles.columns}>
        <section className={`${paper} ${styles.particulars}`} aria-labelledby="particulars-title">
          <h2 id="particulars-title" className={styles.sectionTitle}>
            {t('profile.particulars')}
          </h2>
          <p className={styles.nickname}>{me.nickname}</p>
          <p className={styles.displayName}>{me.displayName}</p>
          <Vignette className={styles.vignette} />
          <dl className={styles.facts}>
            <dt>{t('profile.email')}</dt>
            <dd>{me.email ?? t('profile.emailUnknown')}</dd>
          </dl>
          <p className={styles.since}>
            {t('profile.serviceSince', { date: dateFormat.format(new Date(me.createdAt)) })}
          </p>
        </section>

        <div className={styles.stack}>
          <RegisterForm me={me} />
          <LanguageSection me={me} />
        </div>
      </div>
    </div>
  );
}

function RegisterForm({ me }: { me: MeDto }) {
  const { t } = useTranslation();
  const update = useUpdateMe();
  const [nickname, setNickname] = useState(me.nickname);
  const [displayName, setDisplayName] = useState(me.displayName);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<'idle' | 'saved' | 'failed'>('idle');
  const [failure, setFailure] = useState<string | null>(null);
  const ids = { nickname: useId(), displayName: useId(), status: useId() };

  /** Maps a zod issue (client-side) or a server-side validation message to a localised hint. */
  const messageFor = (issue: { code?: string; message?: string } | undefined): string | undefined => {
    if (!issue) return undefined;
    if (issue.message === 'nickname_charset') return t('errors.nickname_charset');
    if (issue.code === 'too_small' || /too small/i.test(issue.message ?? '')) return t('errors.too_short');
    if (issue.code === 'too_big' || /too big/i.test(issue.message ?? '')) return t('errors.too_long');
    return t('errors.validation_failed');
  };

  async function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('idle');
    setFailure(null);

    const patch: Record<string, unknown> = {};
    if (nickname.trim() !== me.nickname) patch.nickname = nickname;
    if (displayName.trim() !== me.displayName) patch.displayName = displayName;
    if (Object.keys(patch).length === 0) {
      setStatus('saved');
      return;
    }

    const parsed = UpdateMeBody.safeParse(patch);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        next[key] ??= messageFor(issue);
      }
      setErrors(next);
      return;
    }
    setErrors({});

    try {
      const saved = await update.mutateAsync(parsed.data);
      // Show exactly what the server stored (e.g. trimmed values).
      setNickname(saved.nickname);
      setDisplayName(saved.displayName);
      setStatus('saved');
    } catch (error) {
      setStatus('failed');
      if (error instanceof ApiRequestError && error.code === 'nickname_taken') {
        setErrors({ nickname: t('errors.nickname_taken') });
      } else if (error instanceof ApiRequestError && error.code === 'validation_failed') {
        const fields = error.fieldErrors();
        setErrors({
          nickname: messageFor(fields.nickname === undefined ? undefined : { message: fields.nickname }),
          displayName: messageFor(
            fields.displayName === undefined ? undefined : { message: fields.displayName },
          ),
        });
      } else if (error instanceof ApiRequestError && error.code === 'network') {
        setFailure(t('errors.network'));
      } else {
        setFailure(t('errors.generic'));
      }
    }
  }

  return (
    <section className={paper} aria-labelledby="register-title">
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        <div>
          <h2 id="register-title" className={styles.sectionTitle}>
            {t('profile.register')}
          </h2>
          <p className={form.hint}>{t('profile.registerHint')}</p>
        </div>

        <TextField
          id={ids.nickname}
          label={t('profile.nickname')}
          hint={t('profile.nicknameHint')}
          value={nickname}
          onChange={setNickname}
          error={errors.nickname}
          maxLength={NICKNAME_MAX + 4}
          autoComplete="nickname"
        />
        <TextField
          id={ids.displayName}
          label={t('profile.displayName')}
          hint={t('profile.displayNameHint')}
          value={displayName}
          onChange={setDisplayName}
          error={errors.displayName}
          maxLength={DISPLAY_NAME_MAX + 4}
          autoComplete="name"
        />

        <div className={styles.submitRow}>
          <button type="submit" className={button({ tone: 'seal' })} disabled={update.isPending}>
            {update.isPending ? t('profile.saving') : t('profile.save')}
          </button>
          <p id={ids.status} role="status" aria-live="polite" className={styles.status}>
            {status === 'saved' ? t('profile.saved') : null}
            {status === 'failed' && failure ? <span className={form.error}>{failure}</span> : null}
          </p>
        </div>
      </form>
    </section>
  );
}

function TextField(props: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  error: string | undefined;
  maxLength: number;
  autoComplete: string;
}) {
  const hintId = `${props.id}-hint`;
  const errorId = `${props.id}-error`;
  return (
    <div className={form.field}>
      <label htmlFor={props.id} className={form.label}>
        {props.label}
      </label>
      <input
        id={props.id}
        className={form.input}
        value={props.value}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
        maxLength={props.maxLength}
        autoComplete={props.autoComplete}
        spellCheck={false}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? `${errorId} ${hintId}` : hintId}
      />
      {props.error ? (
        <p id={errorId} className={form.error}>
          {props.error}
        </p>
      ) : null}
      <p id={hintId} className={form.hint}>
        {props.hint}
      </p>
    </div>
  );
}

function LanguageSection({ me }: { me: MeDto }) {
  const { t, i18n } = useTranslation();
  const update = useUpdateMe();

  function choose(locale: Locale) {
    void i18n.changeLanguage(locale);
    if (locale !== me.locale) update.mutate({ locale });
  }

  return (
    <section className={paper} aria-labelledby="language-title">
      <fieldset className={styles.fieldset}>
        <legend>
          <h2 id="language-title" className={styles.sectionTitle}>
            {t('profile.languageTitle')}
          </h2>
        </legend>
        <p className={form.hint}>{t('profile.languageHint')}</p>
        <div className={`${panel} ${styles.languages}`}>
          {LOCALES.map((locale) => (
            <label key={locale} className={styles.languageOption} lang={locale}>
              <input
                type="radio"
                name="locale"
                value={locale}
                checked={me.locale === locale}
                onChange={() => {
                  choose(locale);
                }}
              />
              {ENDONYMS[locale]}
            </label>
          ))}
        </div>
      </fieldset>
    </section>
  );
}
